import React, { useState, useEffect, useCallback } from 'react';
import { Box, Container, useMediaQuery, useTheme } from '@mui/material';
import { Outlet, useParams } from 'react-router-dom';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import ConversationsList from './components/ConversationsList';
import MessageHeader from './components/MessageHeader';
import EmptyState from './components/EmptyState';
import { MetaTags } from '../../components/Metatags/Metatags';
import { useMessageContext } from '../../hook/useMessageContext';
import { useDispatch, useSelector } from 'react-redux';
import { roomsPaginationSelector, roomsSelector } from '../../selectors/selectors';
import { getMessagesForSelectedRoom } from '../../redux/messages/actions';
import { getJWtDetails } from '../../utils/AppUtils';

const Messages: React.FC = () => {
    const { roomId } = useParams();
    const { themeStyle } = useThemeContext();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const currentRooms = useSelector(roomsSelector);
    const pagination = useSelector(roomsPaginationSelector);
    const dispatch = useDispatch();
    const { user_id } = getJWtDetails();

    const [rooms, setRooms] = useState(currentRooms);
    const { activeRoomId, setActiveRoomId, setIsLoading, limit, page } = useMessageContext();

    // Set active room when roomId param changes
    useEffect(() => {
        if (roomId) {
            setActiveRoomId(roomId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomId]);

    useEffect(() => {
        if (currentRooms) setRooms(currentRooms);
    }, [currentRooms]);

    const handleGetRoomMessages = useCallback(() => {
        setIsLoading(true);
        if (activeRoomId) {
            dispatch(
                getMessagesForSelectedRoom({
                    roomID: activeRoomId,
                    userID: user_id,
                    pagination: {
                        limit: limit,
                        page: page,
                    },
                }),
            );
        }
        setIsLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeRoomId]);

    useEffect(() => {
        handleGetRoomMessages();
    }, [handleGetRoomMessages]);

    useEffect(() => {
        window.addEventListener('popstate', event => {
            if (event.state) {
                setActiveRoomId(null);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <MetaTags title="Your Conversations & Messages" description="">
            <Container
                // disableGutters
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: { xs: 0, sm: -65, xl: -100 },
                    right: 0,
                    bottom: 0,
                    marginLeft: { xs: '0px', sm: '65px', mdlg: '80px', xl: '100px' },
                    marginRight: { xs: 0, sm: -300 },
                    // paddingLeft: '0px',
                    // paddingRight: { xs: '0px', sm: '40px' },
                    // paddingTop: { mdLg: '10px' },
                    overflowX: 'auto',
                    minHeight: '100dvh',
                    //  height: '100dvh',
                    width: '100dvw',
                    minWidth: '100dvw',
                    zIndex: 9999,
                }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '64px',
                        zIndex: 10,
                    }}>
                    <MessageHeader />
                </Box>

                <Box
                    sx={{
                        position: 'absolute',
                        top: '64px',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        gap: 0,
                        overflow: 'hidden',
                    }}>
                    {/* Conversations list - hidden on mobile when a conversation is active */}
                    {(!isMobile || !activeRoomId) && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                bottom: 0,
                                width: { xs: '100%', md: '33.333%', lg: '25%' },
                                borderRight: `1px solid ${theme[themeStyle].grey}`,
                                overflow: 'hidden',
                                zIndex: 5,
                            }}>
                            <ConversationsList
                                rooms={rooms}
                                activeRoomId={activeRoomId}
                                onRoomClicked={roomId => setActiveRoomId(roomId)}
                                pagination={pagination}
                            />
                        </Box>
                    )}

                    {/* Message content area */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: { xs: 0, md: '33%', lg: '25%' },
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            zIndex: 1,
                        }}>
                        {activeRoomId ? (
                            <Outlet context={{ roomId: activeRoomId }} />
                        ) : (
                            <EmptyState
                                message="Select a conversation or start a new one"
                                subMessage="Your messages are end-to-end encrypted"
                            />
                        )}
                    </Box>
                </Box>
            </Container>
        </MetaTags>
    );
};

export { Messages };
